import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import Icon, { IconName } from '../icons/icon/icon-component';
import styles from './toast-component.module.scss';

type ToastProps = {
  showToast?: boolean;
  backgroundColour?: 'success' | 'warning' | 'error' | 'information' | 'stock' | string;
  text: string;
  icon?: IconName;
  isFullWidth?: boolean;
  /**Number of seconds that toast is visible */
  duration?: number;
  onCloseCallback?: () => void;
};

/**
 * Toast component
 *
 * https://www.figma.com/file/gCI0XEwtC0PHZQA9WfXfgX/Responsive-Basket-MVP?node-id=1751%3A319771
 */
export const Toast: FunctionComponent<ToastProps> = ({ showToast, backgroundColour = 'success', duration = 8.5, ...props }) => {
  const [isToastVisible, setIsToastVisible] = useState(showToast);
  const [lastShowToastProp, setLastShowToastProp] = useState(showToast);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (lastShowToastProp !== showToast) {
    const isShowingToast = showToast ?? false;
    setIsToastVisible(isShowingToast);
    setLastShowToastProp(showToast);
  }

  const closeToast = () => {
    setIsToastVisible(false);
  };

  useEffect(() => {
    if (!isToastVisible) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // start fresh timer
    timerRef.current = setTimeout(() => {
      closeToast();
      props.onCloseCallback?.();
    }, duration * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isToastVisible, duration, props]);

  return (
    <div
      className={`${styles['toast-container']} ${isToastVisible ? styles.visible : styles.hide} ${
        props.isFullWidth ? styles.fullscreen : ''
      } ${styles[backgroundColour ?? 'success']}`}
    >
      <div className={`${styles.toast} ${props.isFullWidth ? styles.fullscreen : ''}`}>
        {props.icon && (
          <div className={`${styles['icon-container']} ${props.icon === 'info' ? styles['info-icon'] : ''}`}>
            <Icon
              name={props.icon}
              className={`${styles.icon} ${props.icon === 'info' ? styles['info-icon'] : ''}`}
              width={24}
              height={24}
            />
          </div>
        )}
        <span className={styles['toast-text']}>{props.text}</span>
      </div>
      <div className={isToastVisible ? styles['load-bar'] : ''} style={{ '--load-duration': `${duration}s` } as CSSProperties}></div>
      <Icon data-testid='toast-close-button' name='close' className={`${styles['close-icon']}`} onClick={closeToast} />
    </div>
  );
};

export default Toast;
